import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "sonner";
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, IconAlert, IconButton, IconClose, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {useCountSegmentAccounts} from "../../apis/requests/segments/countSegmentAccounts";
import {useGetSegmentsSchema} from "../../apis/requests/segments/getSegmentsSchema";
import {
  ConditionGroup,
  ReachSegment,
  ReachSegmentCreatePayload,
  SegmentCategory,
} from "../../apis/requests/segments/types";
import ConditionsCard from "./segment-form/ConditionsCard";
import DetailsCard from "./segment-form/DetailsCard";
import {DEFAULT_SEGMENT_TYPE, VALIDATION_SEGMENT_TYPE} from "./segment-form/constants";
import {buildConditionsPayload, firstErrorMessage} from "./segment-form/helpers";
import {formSchema, FormValues} from "./segment-form/schema";

type Props = {
  segment?: ReachSegment;
  category: SegmentCategory;
  onStage: (payload: ReachSegmentCreatePayload) => void;
};

const mapGroupsFromApi = (conditions: ConditionGroup): FormValues["groups"] => {
  if (!conditions.rules.length) {
    return [{name: "", match_mode: "all", conditions: []}];
  }
  return conditions.rules.map((group) => ({
    name: group.name ?? "",
    match_mode: group.match_mode,
    conditions: group.rules.map((c) => ({
      parameter: c.parameter,
      operator: c.operator,
      value: c.value,
    })),
  }));
};

const SegmentDialog = NiceModal.create(({segment: existing, category, onStage}: Props) => {
  const isEdit = !!existing;
  const modal = useModal();
  const {data: schema} = useGetSegmentsSchema();
  const {mutateAsync: countAsync, data: countData, isPending: isCounting} = useCountSegmentAccounts();

  const parameters = schema?.parameters ?? [];
  const defaultType = category === "validation" ? VALIDATION_SEGMENT_TYPE : DEFAULT_SEGMENT_TYPE;

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      name: existing?.name ?? "",
      description: existing?.description ?? "",
      type: existing?.type ?? defaultType,
      group_match_mode: existing?.conditions.match_mode ?? "all",
      groups: existing?.conditions
        ? mapGroupsFromApi(existing.conditions)
        : [{name: "", match_mode: "all" as const, conditions: []}],
    },
  });
  const {
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: {isDirty, errors},
  } = methods;

  const nameValue = watch("name");
  const issue = firstErrorMessage(errors);

  const onPreviewCount = async () => {
    const conditionsValid = await trigger("groups");
    if (!conditionsValid) return;
    try {
      await countAsync(buildConditionsPayload(getValues(), parameters));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to count matching accounts");
    }
  };

  const onSubmit = handleSubmit((values) => {
    onStage({
      name: values.name.trim(),
      description: values.description.trim() || null,
      priority: existing?.priority ?? 0,
      type: values.type,
      conditions: buildConditionsPayload(values, parameters),
    });
    modal.hide();
  });

  const stageDisabled = !nameValue?.trim() || (isEdit && !isDirty);

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {
          sx: {
            borderRadius: "16px",
            maxWidth: 900,
            width: "100%",
            backgroundColor: Colors.bw.white,
          },
        },
      }}
      maxWidth={false}
    >
      <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
          {isEdit ? "Edit segment" : "New segment"}
        </Text>
        <IconButton
          onClick={() => modal.hide()}
          size="small"
          sx={{
            color: Colors.grey._500,
            "&:hover": {backgroundColor: Colors.tertiary._200},
          }}
        >
          <IconClose width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{p: "0 24px 24px"}}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={onSubmit} sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <DetailsCard category={category} />
            <ConditionsCard parameters={parameters} />
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions
        sx={{p: "16px 24px", justifyContent: "space-between", borderTop: `1px solid ${Colors.tertiary._200}`}}
      >
        <Container row sx={{gap: 1.5, alignItems: "center", minHeight: 32}}>
          <Button
            buttonType="outlined-primary"
            size="small"
            onClick={onPreviewCount}
            disabled={isCounting}
            sx={{
              borderColor: Colors.tertiary._300,
              color: Colors.grey._800,
              "&:hover": {borderColor: Colors.tertiary._400, backgroundColor: Colors.grey._100},
            }}
          >
            {isCounting ? <CircularProgress size={16} /> : "Preview matching accounts"}
          </Button>
          {issue ? (
            <Container row sx={{gap: 1, alignItems: "center"}}>
              <IconAlert width={18} height={18} color={Colors.red._900} />
              <Text size={13} sx={{color: Colors.red._900}}>
                {issue}
              </Text>
            </Container>
          ) : (
            countData && (
              <Text size={13} sx={{color: Colors.grey._700}}>
                {countData.count.toLocaleString()} accounts match
              </Text>
            )
          )}
        </Container>
        <Container row sx={{gap: 1}}>
          <Button
            buttonType="outlined-primary"
            size="small"
            onClick={() => modal.hide()}
            sx={{
              borderColor: Colors.tertiary._300,
              color: Colors.grey._800,
              "&:hover": {borderColor: Colors.tertiary._400, backgroundColor: Colors.grey._100},
            }}
          >
            Cancel
          </Button>
          <Button buttonType="contained-primary" size="small" onClick={onSubmit} disabled={stageDisabled}>
            {isEdit ? "Apply changes" : "Add segment"}
          </Button>
        </Container>
      </DialogActions>
    </Dialog>
  );
});

export default SegmentDialog;
